// Leads (CRM) e o catálogo de status do lead por marca. Duas features do back
// (`leads` e `lead-statuses`) num módulo só: o status de um lead É uma entrada
// desse catálogo, e as duas telas que mexem em status leem as duas coisas —
// separá-las daria dois módulos que só se importam.
import { api, toApiError } from "./client.js";

// Espelha a feature `leads` do back (/v1/leads). Leads chegam pela ingestão do
// HubSpot (webhook) e ficam disponíveis para leitura escopada por papel: seller
// vê só os próprios, diretor/gerente veem os da org. Datas chegam como ISO.

// Legado: as keys default do catálogo. Os status agora são CUSTOMIZÁVEIS por org
// (catálogo lead_status) — a UI usa o catálogo (useLeadStatuses), não estas
// constantes. Mantidas só como fallback de rótulo p/ keys default sem catálogo.
export const LEAD_STATUSES = ["NEW", "IN_CONTACT", "QUALIFIED", "WON", "LOST"] as const;
export type LeadStatus = (typeof LEAD_STATUSES)[number];

export const LEAD_STATUS_LABELS: Record<string, string> = {
  NEW: "Novo",
  IN_CONTACT: "Em contato",
  QUALIFIED: "Qualificado",
  WON: "Ganho",
  LOST: "Perdido",
};

// --- Catálogo de status do lead por org (lead_status) ---
// Espelha a feature `lead-statuses` do back. Cada org define seus status.

export const STATUS_COLORS = [
  "sky",
  "amber",
  "violet",
  "emerald",
  "destructive",
  "muted",
  "rose",
  "cyan",
] as const;
export type StatusColor = (typeof STATUS_COLORS)[number];

export interface LeadStatusDef {
  id: string;
  organizationId: string;
  key: string;
  label: string;
  color: string;
  queueWeight: number;
  sortOrder: number;
  isDefault: boolean;
  active: boolean;
  // LEGADO: valores de `etapa_discador`, a propriedade que classificava o lead
  // até 17/08/2026. Não decide mais nada — fica como histórico do mapa antigo.
  hubspotStages: string[];
  // LEGADO: valores de `etapa` (rótulo de texto) que alimentavam este status
  // até o casamento passar a ser por id. Continua existindo como fallback no
  // back para quem ainda não migrou — a tela mostra em seção somente-leitura.
  crmStages: string[];
  // Etapas do CRM que alimentam este status na ingestão, por id do
  // `dealstage` do HubSpot (não por rótulo). É o que decide de fato; o label
  // vai junto só para exibição.
  crmStageIds: { id: string; label: string }[];
  createdAt: string;
  updatedAt: string;
}

// GET /v1/lead-statuses — catálogo da org (semeado no back se vazio).
export async function getLeadStatuses(includeInactive = false): Promise<LeadStatusDef[]> {
  const { data, error } = await api<{ leadStatuses: LeadStatusDef[] }>("/v1/lead-statuses", {
    query: includeInactive ? { includeInactive: true } : {},
  });
  if (error) throw toApiError("GET /v1/lead-statuses", error);
  return data.leadStatuses;
}

export interface LeadStatusInput {
  key: string;
  label: string;
  color?: StatusColor;
  queueWeight?: number;
  sortOrder?: number;
  isDefault?: boolean;
  hubspotStages?: string[];
  crmStages?: string[];
  crmStageIds?: { id: string; label: string }[];
}

// POST /v1/lead-statuses — cria um status (gestor+).
export async function createLeadStatusDef(input: LeadStatusInput): Promise<LeadStatusDef> {
  const { data, error } = await api<LeadStatusDef>("/v1/lead-statuses", {
    method: "POST",
    body: input,
  });
  if (error) throw toApiError("POST /v1/lead-statuses", error);
  return data;
}

// POST /v1/lead-statuses/reset — restaura os cinco status padrão da organização.
export async function resetLeadStatusDefs(): Promise<LeadStatusDef[]> {
  const { data, error } = await api<{ leadStatuses: LeadStatusDef[] }>("/v1/lead-statuses/reset", {
    method: "POST",
  });
  if (error) throw toApiError("POST /v1/lead-statuses/reset", error);
  return data.leadStatuses;
}

// PATCH /v1/lead-statuses/:id — edita um status (a key é imutável).
export async function updateLeadStatusDef(
  id: string,
  patch: Partial<Omit<LeadStatusInput, "key"> & { active: boolean }>,
): Promise<LeadStatusDef> {
  const { data, error } = await api<LeadStatusDef>(`/v1/lead-statuses/${id}`, {
    method: "PATCH",
    body: patch,
  });
  if (error) throw toApiError(`PATCH /v1/lead-statuses/${id}`, error);
  return data;
}

// DELETE /v1/lead-statuses/:id — soft-delete (gestor+).
export async function deleteLeadStatusDef(id: string): Promise<void> {
  const { error } = await api(`/v1/lead-statuses/${id}`, { method: "DELETE" });
  if (error) throw toApiError(`DELETE /v1/lead-statuses/${id}`, error);
}

// POST /v1/leads/reprocess-status — re-deriva o status dos leads pela etapa
// (gestor+), do mais antigo ao mais novo. Retorna quantos foram reprocessados.
export async function reprocessLeadStatuses(): Promise<{
  reprocessed: number;
  scanned: number;
}> {
  const { data, error } = await api<{ reprocessed: number; scanned: number }>(
    "/v1/leads/reprocess-status",
    { method: "POST" },
  );
  if (error) throw toApiError("POST /v1/leads/reprocess-status", error);
  return data;
}

// Lead enriquecido com o nome do responsável (join no back). Datas em ISO.
export interface LeadListItem {
  id: string;
  name: string | null;
  email: string | null;
  phone: string | null;
  // Telefone mascarado por papel: para o vendedor `phone` vem null e
  // `phoneHidden=true` (o número do lead não é exposto ao seller). `hasPhone`
  // diz se o lead TEM número (fila do discador) sem revelá-lo — a ligação é
  // iniciada por `leadId`.
  phoneHidden: boolean;
  hasPhone: boolean;
  // Deep-link pronto para o registro do lead no HubSpot, montado no back a partir
  // do portal (env HUBSPOT_PORTAL_ID) + lead_external_id. Vem null quando não há
  // portal configurado ou o lead não tem external id — nesse caso o botão "Abrir
  // no HubSpot" não aparece. O front nunca conhece o formato da URL nem o portal.
  hubspotUrl: string | null;
  stage: string | null;
  // Id do `dealstage` no HubSpot. Opcional durante rollout: versões anteriores
  // do back não enviam o campo. Serve de fallback de exibição — o rótulo é
  // opcional no contrato de ingestão (o HubSpot não entrega rótulo ao workflow),
  // então sem ele a ficha mostra o id em vez de um campo vazio.
  stageId?: string | null;
  // Opcional durante rollout: versões anteriores do back não enviam o campo.
  dialerStage?: string | null;
  status: string;
  assignedTo: string | null;
  assigneeName: string | null;
  source: string;
  createdAt: string;
  updatedAt: string;
}

export interface LeadProgressItem {
  id: string;
  fromStage: string | null;
  toStage: string | null;
  fromDialerStage: string | null;
  toDialerStage: string | null;
  fromStatus: string | null;
  toStatus: string;
  source: string;
  occurredAt: string | null;
  createdAt: string;
}

export interface LeadDetail extends LeadListItem {
  progress: LeadProgressItem[];
}

export interface LeadsResponse {
  leads: LeadListItem[];
  viewerRole: string;
}

export interface LeadsQuery {
  status?: string;
  assignedTo?: string;
  q?: string;
  limit?: number;
  offset?: number;
}

export interface NewLeadInput {
  name: string;
  phone: string;
  email?: string | null;
  status?: string | null;
  assignedTo?: string | null;
}

export interface ImportLeadsResult {
  created: number;
  ignored: number;
  errors: Array<{ line: number; reason: string }>;
}

// GET /v1/leads/by-phone — preview da associação que o backend fará ao iniciar
// uma discagem manual. O telefone do lead continua sujeito ao mascaramento por papel.
export async function getLeadByPhone(phone: string): Promise<LeadListItem | null> {
  const { data, error } = await api<{ lead: LeadListItem | null }>("/v1/leads/by-phone", {
    query: { phone },
  });
  if (error) throw toApiError("GET /v1/leads/by-phone", error);
  return data.lead;
}

// GET /v1/leads — lista os leads da org (escopo por papel no back: seller vê só
// os próprios). `q` busca em nome/telefone/email.
export async function getLeads(params: LeadsQuery = {}): Promise<LeadsResponse> {
  const { data, error } = await api<LeadsResponse>("/v1/leads", {
    query: params,
  });
  if (error) throw toApiError("GET /v1/leads", error);
  return data;
}

// GET /v1/leads/:id — um lead da org (seller só os próprios; senão 404).
export async function getLead(id: string): Promise<LeadDetail> {
  const { data, error } = await api<LeadDetail>(`/v1/leads/${id}`);
  if (error) throw toApiError(`GET /v1/leads/${id}`, error);
  return data;
}

// PATCH /v1/leads/:id/status — muda o status de discagem. Seller nos próprios;
// gestor em qualquer. Devolve o lead atualizado.
export async function updateLeadStatus(id: string, status: string): Promise<LeadListItem> {
  const { data, error } = await api<LeadListItem>(`/v1/leads/${id}/status`, {
    method: "PATCH",
    body: { status },
  });
  if (error) throw toApiError(`PATCH /v1/leads/${id}/status`, error);
  return data;
}

// PATCH /v1/leads/:id/assignee — reatribui o responsável (só gestor). `null`
// desatribui. Devolve o lead atualizado.
export async function assignLead(id: string, assignedTo: string | null): Promise<LeadListItem> {
  const { data, error } = await api<LeadListItem>(`/v1/leads/${id}/assignee`, {
    method: "PATCH",
    body: { assignedTo },
  });
  if (error) throw toApiError(`PATCH /v1/leads/${id}/assignee`, error);
  return data;
}

// DELETE /v1/leads/:id — apaga o lead (só gestor).
export async function deleteLead(id: string): Promise<void> {
  const { error } = await api(`/v1/leads/${id}`, { method: "DELETE" });
  if (error) throw toApiError(`DELETE /v1/leads/${id}`, error);
}

// POST /v1/leads — cadastro manual (só gestor). 409 quando o telefone já existe
// na marca; o corpo do erro traz `leadId` para a tela oferecer abrir o existente.
export async function createLead(input: NewLeadInput): Promise<LeadListItem> {
  const { data, error } = await api<LeadListItem>("/v1/leads", { method: "POST", body: input });
  if (error) throw toApiError("POST /v1/leads", error);
  return data;
}

// POST /v1/leads/import — lote de até 1000 linhas. `dryRun` valida sem gravar:
// é o que alimenta o preview do CSV.
export async function importLeads(
  rows: NewLeadInput[],
  dryRun = false,
): Promise<ImportLeadsResult> {
  const { data, error } = await api<ImportLeadsResult>("/v1/leads/import", {
    method: "POST",
    body: { rows, dryRun },
  });
  if (error) throw toApiError("POST /v1/leads/import", error);
  return data;
}

// Dados ricos do lead para o card do discador. Mora AQUI, e não na seção do
// discador de onde saiu, porque a rota é /v1/leads/:id/dados: o discador é só
// quem consome. `fonte` diz se o HubSpot respondeu nesta leitura ou se veio só
// do snapshot salvo — o card mostra isso em vez de fingir que o dado é fresco.
export interface LeadDados {
  dados: Record<string, string>;
  fonte: "hubspot" | "salvo";
}

export async function getLeadDados(leadId: string): Promise<LeadDados> {
  const { data, error } = await api<LeadDados>(`/v1/leads/${encodeURIComponent(leadId)}/dados`);
  if (error) throw toApiError("GET /v1/leads/:id/dados", error);
  return data;
}
